import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { OmittedBaseDto, TDtoWrapper, ValueType } from '@/shared/lib/forms/form-wrapper.types';
import { CollectionName, CoOwner } from '@/shared/lib/sj-orm';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';

import { useDynamicFields } from '../hooks/useDynamicFields';

import { RenderQuestionComponent } from './RenderQuestionsComponent';

type CoOwnerHelp = {
  coOwnerEntityDtoId: QuestionType.PICK_ARRAY_OF_DTO;
  coOwnerDocument: QuestionType.PICK_ARRAY_OF_DTO;
  coOwnerShare: QuestionType.NUMBER;
};

const questions: TDtoWrapper<CoOwner, CoOwnerHelp> = {
  coOwnerEntityDtoId: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.coOwnerEntityDtoId.label',
    items: [CollectionName.BENEFICIARIES, CollectionName.CONTACTS],
    order: 11,
    makeExtFilter: ({ ctxEntity, fieldName }) => {
      return (item) => {
        const itemId = item.id;
        const currentFieldValue = ctxEntity?.[fieldName];

        if (itemId === currentFieldValue) return true;
        if (!ctxEntity) return false;

        const selectedEntitiesIds = Object.keys(ctxEntity)
          .filter((key) => key.includes('coOwnerEntityDtoId'))
          .map((key) => ctxEntity[key]);

        const isAlreadySelected = selectedEntitiesIds.includes(itemId);
        return !isAlreadySelected;
      };
    },
  },
  coOwnerDocument: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.coOwnerDocument.label',
    items: [CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, CollectionName.PRIVATE_DOCUMENTS],
    mode: 'multiple',
    order: 11,
    deps: ['coOwnerEntityDtoId'],
  },
  coOwnerShare: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:common.inputs.coOwnerShare.label',
    order: 11,
    deps: ['coOwnerEntityDtoId'],
  },
};

type FieldsArrayProps = {
  name: string;
  label: string;
};

export const CoOwnersField: FC<FieldsArrayProps> = ({ name, label }) => {
  const { t } = useTranslation('forms_assets');
  const { control } = useFormContext();
  const { fields, handleAdd, remove } = useDynamicFields({ name, control, items: questions });

  return (
    <Container>
      <Header>
        <Title bold level={5}>
          {label}
        </Title>
        <Button
          size="middle"
          icon={<Icon name="plus" size="s20" />}
          type="text"
          style={{ height: 20 }}
          onClick={handleAdd}
        >
          {t('forms_assets:common.inputs.coOwners.button')}
        </Button>
      </Header>
      <ItemsContainer>
        {fields.map((field, index) => (
          <Item key={field.id}>
            {Object.entries(questions).map(([key, value]) => {
              const question = value as ValueType<
                QuestionType,
                TDtoWrapper<OmittedBaseDto, Record<string, QuestionType>>
              >;

              return (
                <QuestionWrapper key={key}>
                  <RenderQuestionComponent
                    id={`${name}.${index}.${key}`}
                    label={question.label}
                    question={question}
                  />
                </QuestionWrapper>
              );
            })}

            <DeleteButton
              icon={<Icon name="trash" size="s20" />}
              type="default"
              disabled={fields.length <= 1}
              onClick={() => remove(index)}
            />
          </Item>
        ))}
      </ItemsContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  grid-column: 1 / -1;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.isMobile} {
    gap: 32px;
  }
`;

const Item = styled.div`
  display: flex;
  gap: 16px;

  ${media.isMobile} {
    flex-direction: column;
  }
`;

const QuestionWrapper = styled.div`
  flex: 1;
`;

const DeleteButton = styled(Button)`
  &&& {
    height: 100%;
    background: ${COLORS.systemLight200};
    border: none;
    color: ${COLORS.systemDark200};

    &:hover,
    &:disabled {
      background: ${COLORS.systemLight300} !important;
    }

    &:disabled {
      color: ${COLORS.systemDark100};
    }

    ${media.isMobile} {
      width: 100%;
    }
  }
`;
